"use client";

import { AnimatePresence, motion } from "motion/react";
import { MessageSquare, Paperclip } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Icon } from "@iconify-icon/react";
import { useEffect } from "react";

import { useChat } from "@/features/chat/hooks/useChat.tsx";
import useWorkspaceInformationStore from "@/features/workspaces/store/useWorkspaceInformationStore.tsx";
import { ConversationView } from "@/features/chat/components/ConversationView.tsx";
import { Input } from "@/components/ui/input.tsx";
import { EmojiPickerButton } from "@/features/chat/components/EmojiPickerButton.tsx";
import { FilePreview } from "@/features/chat/components/FilePreview";
import { Button } from "@/components/ui/button.tsx";
import useUserStore from "@/features/auth/store/useUserStore.ts";
import GenericLoader from "@/components/GenericLoader.tsx";
import { DragDropOverlay } from "@/features/chat/components/DragDropOverlay.tsx";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { StorageUsageIndicator } from "@/features/chat/components/StorageUsageIndicator";

export const LessonChatCard = () => {
  const { t } = useTranslation(["workspaces", "chat"]);
  const { data: workspaceData } = useWorkspaceInformationStore();
  const {
    messageInput,
    selectedFiles,
    selectedFilesCount,
    isSending,
    isLoading,
    activeConversation,
    messages,
    sendMessage,
    handleKeyPress,
    handleInputChange,
    handleFileChange,
    removeFile,
    handleAttachmentClick,
    insertEmoji,
    activeChannel,
    isDragging,
    handleDragEnter,
    handleDragLeave,
    handleDragOver,
    handleDrop,
    handlePaste,
    openConversation,
    scrollToBottom,
    animatedMessageIds,
    cancelEditing,
    checkIfNearBottom,
    deleteMessage,
    editMessageContent,
    editTextareaRef,
    editingMessageId,
    editingPendingMessageId,
    fetchNextPage,
    formatFileSize,
    handleEditKeyPress,
    hasNextPage,
    isCurrentUser,
    isFetchingNextPage,
    removePendingMessage,

    saveEditedMessage,
    scrollAreaRef,
    setEditMessageContent,
    startEditingMessage,
    startEditingPendingMessage,
    fileInputRef,
    inputRef,
  } = useChat(workspaceData?.main_conversation_id ?? "", "lesson");
  const { user } = useUserStore();
  const MAX_FILE_COUNT = 5;

  useEffect(() => {
    if (workspaceData?.main_conversation_id) {
      openConversation(workspaceData?.main_conversation_id);
    }
  }, []);
  useEffect(() => {
    scrollToBottom(true);
  }, []);

  return (
    <motion.div
      animate={{ opacity: 1, y: 0 }}
      className="bg-foreground/50 text-accent-foreground relative z-50 col-span-12 row-span-2 h-fit rounded-2xl border-none p-4 shadow-2xl sm:p-6 lg:col-span-4"
      initial={{ opacity: 0, y: 20 }}
      transition={{ duration: 0.4, delay: 0.1 }}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      <DragDropOverlay isDragging={isDragging} />
      {isLoading && <GenericLoader />}
      <div className="mb-3 flex items-center justify-between sm:mb-4">
        <h3 className="flex items-center gap-3 text-lg font-bold sm:text-xl">
          <div className="flex h-8 w-8 items-center justify-center rounded-xl bg-blue-500/20 sm:h-10 sm:w-10">
            <MessageSquare className="h-4 w-4 text-blue-400 sm:h-5 sm:w-5" />
          </div>
          {t("workspaces:chat", "Chat")}
        </h3>
      </div>
      <div className="flex h-full flex-col">
        <div className="*:h-[320px] sm:*:h-[400px] lg:*:h-[540px]">
          {messages ? (
            <ConversationView
              activeConversation={activeConversation}
              animatedMessageIds={animatedMessageIds}
              cancelEditing={cancelEditing}
              checkIfNearBottom={checkIfNearBottom}
              currentUser={user}
              deleteMessage={deleteMessage}
              editMessageContent={editMessageContent}
              editTextareaRef={editTextareaRef}
              editingMessageId={editingMessageId}
              editingPendingMessageId={editingPendingMessageId}
              fetchNextPage={fetchNextPage}
              formatFileSize={formatFileSize}
              handleEditKeyPress={handleEditKeyPress}
              hasNextPage={hasNextPage}
              isCurrentUser={isCurrentUser}
              isFetchingNextPage={isFetchingNextPage}
              isLoading={isLoading}
              messages={messages}
              mode={"compact"}
              removePendingMessage={removePendingMessage}
              renderSpecialGroupsAsNormal={true}
              saveEditedMessage={saveEditedMessage}
              scrollAreaRef={scrollAreaRef}
              scrollToBottom={scrollToBottom}
              setEditMessageContent={setEditMessageContent}
              startEditingMessage={startEditingMessage}
              startEditingPendingMessage={startEditingPendingMessage}
            />
          ) : (
            <div />
          )}
        </div>

        {/* Chat input */}
        <div className="p-2 pt-3 sm:pt-4">
          <AnimatePresence>
            {selectedFilesCount > 0 && (
              <motion.div
                animate={{ opacity: 1, height: "auto", y: 0 }}
                className="mb-2 overflow-hidden"
                exit={{ opacity: 0, height: 0, y: -5 }}
                initial={{ opacity: 0, height: 0, y: -5 }}
              >
                <div className="flex flex-wrap gap-2">
                  {selectedFiles.map((fileData, index) => (
                    <FilePreview
                      key={index}
                      file={fileData.file}
                      index={index}
                      isValid={fileData.isValid}
                      validationError={fileData.validationError}
                      onRemove={removeFile}
                    />
                  ))}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
          <div className="bg-muted/20 flex gap-2 rounded-xl p-2.5 backdrop-blur-sm transition-all focus-within:ring-2 focus-within:ring-blue-500/50 sm:p-3">
            {/* Hidden file input */}
            <input
              ref={fileInputRef}
              multiple
              className="hidden"
              type="file"
              onChange={handleFileChange}
            />
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    className="bg-muted/30 hover:bg-muted/40 h-8 w-6 items-center justify-center rounded-xl p-0 transition-colors sm:h-10 sm:w-8"
                    disabled={!activeChannel() || isLoading || selectedFilesCount >= MAX_FILE_COUNT}
                    type="button"
                    variant="ghost"
                    onClick={handleAttachmentClick}
                  >
                    <Paperclip className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="top">{t("chat:attachments.attachFile")}</TooltipContent>
              </Tooltip>
            </TooltipProvider>

            <Input
              ref={inputRef}
              className="placeholder:text-muted-foreground flex-1 border-none bg-transparent text-sm text-white focus-visible:ring-0 focus-visible:ring-offset-0 sm:text-base"
              disabled={isLoading}
              placeholder={
                isSending ? t("chat:input.sendingPlaceholder") : t("chat:input.typingPlaceholder")
              }
              value={messageInput}
              onChange={handleInputChange}
              onKeyDown={handleKeyPress}
              onPaste={handlePaste}
            />

            {/* Emoji picker */}
            <EmojiPickerButton
              buttonClassName="flex h-8 w-6 sm:h-10 sm:w-8 items-center justify-center rounded-xl bg-muted/30 p-0 transition-colors hover:bg-muted/40"
              onEmojiSelect={insertEmoji}
            />

            <Button
              className="h-8 w-8 rounded-xl bg-blue-500/20 p-0 text-blue-400 transition-colors hover:bg-blue-500/30 sm:h-10 sm:w-10"
              disabled={(!messageInput.trim() && selectedFilesCount === 0) || isLoading}
              type="submit"
              onClick={sendMessage}
            >
              <Icon
                className={isSending ? "animate-spin" : ""}
                height="18"
                icon={isSending ? "lucide:loader-2" : "lucide:send"}
                width="18"
              />
              <span className="sr-only">Send</span>
            </Button>
          </div>

          {/* File upload restrictions info and storage usage */}
          <div className="text-muted-foreground relative mt-1.5 flex flex-col items-center justify-center gap-1 text-center text-[11px]">
            {activeConversation?.storage_usage && (
              <StorageUsageIndicator storageUsage={activeConversation.storage_usage} />
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );
};
